// Game module contract (v1). This is the only runtime surface a game module touches:
// it imports `defineEntity` from "#runtime" and exports meta, inputSchema, init, onJoin,
// onLeave and, optionally, a world-level tick. Everything else (netcode, rooms, camera, input
// capture) is the runtime's.
// See CLAUDE.md for the contract in prose; keep the two in step.

import type { Rng } from "./shared/rng.js";
export type { Rng } from "./shared/rng.js";

export type FieldType = "f32" | "i32" | "bool";
export type InputSchema = Record<string, FieldType>;
export type Input = Record<string, number | boolean>;

export interface Meta {
  name: string;
  world: { w: number; h: number };
  /** The runtime owns the camera. "follow" tracks the owner's entity. */
  camera: "follow";
  players: { max: number };
  tickRate: number;
  /** Optional: name of a replicated numeric field on player entities to rank the leaderboard by. */
  score?: string;
  /**
   * Optional: one line for the controls card the shell shows when a game starts, saying what
   * A and B do here. The runtime already explains moving and the buttons themselves.
   */
  controls?: string;
}

/**
 * Entities are plain data. `id`, `type` and `owner` are set by the runtime; `x`, `y`, `r` are
 * required because the runtime indexes on them (spatial hash, culling, camera).
 * Keys beginning with "_" are reserved for the runtime.
 */
export interface Entity {
  id: number;
  type: string;
  owner?: string;
  x: number;
  y: number;
  r: number;
  [field: string]: unknown;
}

export type SpawnFields = { x: number; y: number; r: number; [field: string]: unknown };

export interface EntityDef<E extends Entity = Entity> {
  /** Fields the runtime syncs to clients. x, y, r are always included. Numeric fields interpolate. */
  replicated: string[];
  /**
   * The replicated fields never change after spawn. The runtime sends them to each client once
   * and never diffs them again (removal still works). The entity may still tick private fields
   * and despawn; changing a replicated field is a contract violation the validator catches.
   */
  static?: boolean;
  /** Draw order. Lower layers draw first (underneath); within a layer, smaller `r` first, then id. Default 0. */
  layer?: number;
  /** Initial fields for a new entity. Server only. Use `rng`, never Math.random. */
  spawn(world: World, ownerId: string | undefined, rng: Rng): SpawnFields;
  /** Advance one tick. Server only; pure over (world, rng). `input` is the owner's latest input. */
  tick?(self: E, world: World, input: Input | undefined, dt: number, rng: Rng): void;
  /** Render. Client only; must not write state. `alpha` is the interpolation fraction, for debugging. */
  draw?(self: E, ctx: CanvasRenderingContext2D, world: ClientWorld, alpha: number): void;
}

/** The server-side world handle passed to module code. All mutation goes through it. */
export interface World {
  readonly meta: Meta;
  readonly w: number;
  readonly h: number;
  /** Ticks since the room started. */
  readonly tick: number;
  /** Simulated seconds since the room started. */
  readonly time: number;
  spawn(type: string, ownerId?: string, overrides?: Record<string, unknown>): Entity;
  despawn(id: number): void;
  get(id: number): Entity | undefined;
  /** All entities, sorted by id. */
  entities(): Entity[];
  ofType(type: string): Entity[];
  count(type?: string): number;
  /** Entities whose bounding circle overlaps the given circle, sorted by id. Index is as of tick start. */
  query(x: number, y: number, radius: number): Entity[];
  playerEntity(playerId: string): Entity | undefined;
  players(): string[];
}

/** The client-side, read-only world handle passed to `draw`. */
export interface ClientWorld {
  readonly meta: Meta;
  readonly w: number;
  readonly h: number;
  /** Your own entity id, if you have one. */
  readonly you: number | undefined;
  /** Render time in server-clock seconds. */
  readonly time: number;
  entities(): Entity[];
  get(id: number): Entity | undefined;
  /** Display name recorded when the player arrived. Never resolved at display time. */
  name(playerId: string | undefined): string;
  /**
   * A sprite from the runtime's library by keyword ("knight", "potion red", "tree pine"), or
   * undefined when nothing answers to it; draw a shape instead in that case. The same keyword
   * always gives the same sprite. Options narrow the search to a style ("pixel-16", "vector")
   * or a pack. Keep one style per game.
   */
  sprite(keyword: string, opts?: SpriteOptions): Sprite | undefined;
}

export interface SpriteOptions {
  /** A style or a prefix of one: "pixel-16", "pixel-18", "pixel-24", "vector", or just "pixel". */
  style?: string;
  pack?: string;
}

export interface Sprite {
  name: string;
  pack: string;
  /** Native size in pixels. */
  w: number;
  h: number;
  /**
   * Draw centred on (x, y), scaled to w × h world units (native size by default), rotated by
   * `angle` radians. Returns false, and draws nothing, while the sheet is still loading.
   */
  draw(ctx: CanvasRenderingContext2D, x: number, y: number, w?: number, h?: number, angle?: number): boolean;
}

export interface GameModule {
  meta: Meta;
  inputSchema: InputSchema;
  entities: Map<string, EntityDef>;
  init(world: World, rng: Rng): void;
  onJoin(world: World, playerId: string, rng: Rng): void;
  onLeave(world: World, playerId: string): void;
  /**
   * Optional world-level tick: runs once per step, before any entity ticks. Server only; pure
   * over (world, rng). Keep timers on `world.tick` or `world.time` (or on an entity), never in
   * module scope. Entities it spawns first act next step, like any other spawn.
   */
  tick?(world: World, dt: number, rng: Rng): void;
}

// ---------------------------------------------------------------------------------------------
// Registration. A module calls defineEntity at top level while it is being evaluated; loadModule
// drains what was registered. Phase 1 moves module evaluation into a sandbox with its own
// registry; until then, load one module at a time.

let pending: Map<string, EntityDef> | null = null;
const loaded = new WeakMap<object, GameModule>();
/** Loads are serialised: two modules evaluating at once would share the pending registry. */
let loadQueue: Promise<unknown> = Promise.resolve();

export function defineEntity<E extends Entity = Entity>(type: string, def: EntityDef<E>): void {
  if (typeof type !== "string" || !type) throw new Error("defineEntity: type must be a non-empty string");
  if (!def || typeof def !== "object") throw new Error(`defineEntity("${type}"): definition must be an object`);
  if (!Array.isArray(def.replicated) || !def.replicated.every((f) => typeof f === "string")) {
    throw new Error(`defineEntity("${type}"): replicated must be an array of field names`);
  }
  for (const f of def.replicated) {
    if (f.startsWith("_")) throw new Error(`defineEntity("${type}"): field "${f}" is reserved (leading underscore)`);
  }
  if (typeof def.spawn !== "function") throw new Error(`defineEntity("${type}"): spawn() is required`);
  if (def.tick !== undefined && typeof def.tick !== "function") throw new Error(`defineEntity("${type}"): tick must be a function`);
  if (def.draw !== undefined && typeof def.draw !== "function") throw new Error(`defineEntity("${type}"): draw must be a function`);
  if (def.static !== undefined && typeof def.static !== "boolean") throw new Error(`defineEntity("${type}"): static must be true or false`);
  if (def.layer !== undefined && !Number.isInteger(def.layer)) throw new Error(`defineEntity("${type}"): layer must be an integer`);
  if (!pending) pending = new Map();
  if (pending.has(type)) throw new Error(`defineEntity("${type}"): defined twice`);
  const replicated = Array.from(new Set(["x", "y", "r", ...def.replicated]));
  pending.set(type, { ...(def as unknown as EntityDef), replicated, static: def.static === true, layer: def.layer ?? 0 });
}

/**
 * Load a game module. Takes a thunk so bundlers can see the import:
 *   const game = await loadModule(() => import("../games/agar/game.js"));
 */
export function loadModule(importer: () => Promise<unknown>): Promise<GameModule> {
  const run = loadQueue.then(async () => {
    pending = null;
    const exports = (await importer()) as Record<string, unknown>;
    if (!exports || typeof exports !== "object") throw new Error("loadModule: module did not evaluate to an object");
    const cached = loaded.get(exports);
    if (cached) return cached;
    const entities = pending ?? new Map<string, EntityDef>();
    pending = null;
    const mod = validateModule(exports, entities);
    loaded.set(exports, mod);
    return mod;
  });
  loadQueue = run.catch(() => undefined);
  return run;
}

function validateModule(ex: Record<string, unknown>, entities: Map<string, EntityDef>): GameModule {
  const meta = ex.meta as Meta;
  if (!meta || typeof meta !== "object") throw new Error("module: `export const meta` is missing");
  if (typeof meta.name !== "string" || !meta.name) throw new Error("module: meta.name must be a non-empty string");
  if (!meta.world || !(meta.world.w > 0) || !(meta.world.h > 0)) throw new Error("module: meta.world needs positive w and h");
  if (meta.camera !== "follow") throw new Error("module: meta.camera must be \"follow\"");
  if (!meta.players || !(meta.players.max >= 1) || !(meta.players.max <= 1000)) throw new Error("module: meta.players.max must be 1..1000");
  if (!(meta.tickRate >= 1) || !(meta.tickRate <= 60)) throw new Error("module: meta.tickRate must be 1..60");
  if (meta.score !== undefined && typeof meta.score !== "string") throw new Error("module: meta.score must be a field name");
  if (meta.controls !== undefined && typeof meta.controls !== "string") throw new Error("module: meta.controls must be a string");

  const schema = ex.inputSchema as InputSchema;
  if (!schema || typeof schema !== "object") throw new Error("module: `export const inputSchema` is missing");
  for (const [k, t] of Object.entries(schema)) {
    if (t !== "f32" && t !== "i32" && t !== "bool") throw new Error(`module: inputSchema.${k} has unknown type "${String(t)}"`);
  }

  for (const fn of ["init", "onJoin", "onLeave"] as const) {
    if (typeof ex[fn] !== "function") throw new Error(`module: export function ${fn} is missing`);
  }
  if (ex.tick !== undefined && typeof ex.tick !== "function") throw new Error("module: export tick must be a function (world, dt, rng)");
  if (entities.size === 0) throw new Error("module: no entities were defined (call defineEntity at top level)");

  const mod: GameModule = {
    meta: { ...meta, world: { w: meta.world.w, h: meta.world.h }, players: { max: meta.players.max } },
    inputSchema: { ...schema },
    entities,
    init: ex.init as GameModule["init"],
    onJoin: ex.onJoin as GameModule["onJoin"],
    onLeave: ex.onLeave as GameModule["onLeave"],
  };
  if (ex.tick !== undefined) mod.tick = ex.tick as GameModule["tick"];
  return mod;
}
